import { doesProductComponentMatch } from '@apps/card/routes/CardCustomizer/usePromotionConfig';
import type { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useGeoBasedLocale } from '@shared/hooks/useGeoLocale';
import { useMemo } from 'react';
import { useCardCategoryContext } from '../CardCategoryProvider';
import type { PropsPayload, ValuePropAsset, ValuePropCarouselAsset } from './propValues.types';
import { ENVELOPES_VALUE_PROP, VALUE_PROPS_PAYLOAD } from './valueProps.constants';

const cardCategoryMatches = (categoriesList: ReadonlyArray<string>, context: { occasion: string; category: StationeryTemplateCategoryEnum }) => {
  return categoriesList.some(category => {
    const categoryComponents = category.split('/');
    const [targetOccasion, targetCategory] = categoryComponents;
    const { category: currentCategory, occasion: currentOccasion } = context;

    if (!doesProductComponentMatch(targetOccasion, currentOccasion)) {
      return false;
    }

    if (!doesProductComponentMatch(targetCategory, currentCategory)) {
      return false;
    }

    return true;
  });
};

/**
 * A tile without `visibleInCountries` is global; one with a list is shown only to viewers whose
 * geo-resolved country is in it. `culture` is an approximation (URL param > stored selection >
 * event locale > browser locale > US), not a real IP lookup — good enough for deciding whether
 * to advertise a regional perk, not for anything that has to be exact.
 */
const isVisibleInCountry = (visibleInCountries: ReadonlyArray<string> | undefined, country: string) => {
  return !visibleInCountries || visibleInCountries.includes(country);
};

/**
 * The value-prop tiles for the current category and the viewer's country.
 *
 * {@link ENVELOPES_VALUE_PROP} is appended only while `printEnvelopeProduct` is on, matching the
 * gate on the page it links to. The flag is skipped outside the tile's categories: resolving it
 * tracks an Amplitude exposure, and this hook runs for every category.
 */
export const useValuePropsPayload = (): PropsPayload => {
  const { currentCategory: category } = useCardCategoryContext();
  const { culture } = useGeoBasedLocale();
  const isEnvelopeCategory = cardCategoryMatches(ENVELOPES_VALUE_PROP.categoryList, { occasion: 'wedding', category });
  const [isEnvelopeProductEnabled] = useIsFeatureEnabled('printEnvelopeProduct', { skip: !isEnvelopeCategory });

  return useMemo(() => {
    const isVisible = (card: ValuePropAsset | ValuePropCarouselAsset) =>
      cardCategoryMatches(card.categoryList, { occasion: 'wedding', category }) && isVisibleInCountry(card.visibleInCountries, culture);

    const authoredProps = isEnvelopeProductEnabled ? [...VALUE_PROPS_PAYLOAD.props, ENVELOPES_VALUE_PROP] : VALUE_PROPS_PAYLOAD.props;

    return {
      ...VALUE_PROPS_PAYLOAD,
      props: authoredProps.filter(isVisible).map(card => ({ ...card, row: 0, column: 0 })),
    };
  }, [category, culture, isEnvelopeProductEnabled]);
};
